import './index.scss';
import {
    Box,
    ButtonLink,
    Callout,
    IconChevronLeftRegular,
    IconWarningRegular,
    NavigationBar,
    skinVars,
    Spinner,
    RowList,
    Stack,
    Text2,
    Text3,
    Text6,
    ThemeVariant,
    useDialog,
    IconInformationUserRegular,
} from '@telefonica/mistica';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import CardWithSwitch, { ICardSwitchEvent } from '../../../components/CartWithSwitch';
import SkeletonMobile from '../../../components/SkeletonMobile';
import { ISwitch } from '../../../models/ConfigureLine';
import { getAllRestrictions, getSIMSInfo, updateRestriction } from '../../../services/ConfigureLineService';
import navigateParams from '../../../utils/navigation/navigateParams';
import { formatMobileNumber } from '../../../utils/dataTransformation/unitsConversion';
import { AxiosError } from 'axios';
import { handleErrorMessage } from '../../../utils/dataTransformation/errorMesssages';
import { hideSpinner, showSnackbar } from '../../../utils/store/features/user';
import { itsMoreThanXminutes } from '../../../utils/dataTransformation/dateConversions';
import { getRoleLine } from '../../../utils/dataTransformation/productConsumption';
import { TrackingEvents } from '../../../utils/dataTransformation/tracking';
import { isWebviewIOS, isWebviewAndroid } from '../../../utils/navigation/controlDevices';
import useUserlight from '../../../utils/hooks/useUserLight';
import { useConfigureLineContext } from '../../../utils/store/context/configure-line-context/ConfigureLineContext';
import { useQuery } from '@tanstack/react-query';
import BreadCrumbsWrapper from '../../../components/BreadcrumbsWrapper';

const BlockedNumberSpecials: React.FC = () => {
    const isLight = useUserlight();
    const dispatch = useDispatch();
    const [loading, setLoading] = useState<boolean>(true);
    const consumptions = JSON.stringify(window.localStorage.getItem('storages'));
    const { t } = useTranslation();
    const { trackingScreenView } = new TrackingEvents();
    const navigate = useNavigate();
    const { state, pathname } = useLocation();
    const [data, setData] = useState<ISwitch[]>([]);
    const { dialog } = useDialog();
    const { lineActive, isConvergent } = useConfigureLineContext();

    const { data: simInfo } = useQuery(['sims-info'], () => (lineActive ? getSIMSInfo(lineActive) : null), {
        enabled: !!lineActive,
    });

    useEffect(() => {
        if (!(isWebviewAndroid || isWebviewIOS)) {
            trackingScreenView();
        }
        if (!simInfo?.productId || simInfo?.productId.trim() === '') return;
        getAllRestrictions(simInfo?.productId!)
            .then((results: any) => {
                const dataRoaming = results.data.filter((item: any) => {
                    return item.restrictionType === 'GENERIC';
                });
                let dataSetRoaming: ISwitch[] = [];
                dataRoaming.forEach((element: any) => {
                    dataSetRoaming.push({
                        restrictionSpecId: element.restrictionSpecId,
                        title: element.title,
                        subtitle: element.description,
                        state: element.active,
                    });
                });
                setData(dataSetRoaming);
                setLoading(false);
            })
            .catch((err) => {
                console.error(err);
                const error = handleErrorMessage(err);
                const _error = { ...error, pathname };
                dispatch(hideSpinner());
                navigate('/error', { state: _error });
            });
    }, [dispatch]);

    const _storage = window.localStorage.getItem('timestamp');

    const handleSwitches = (card: ICardSwitchEvent, index: number) => {
        const { state } = card;
        if (isLight) {
            dispatch(showSnackbar());
        } else {
            if (window.localStorage.getItem('timestamp')) {
                const storage: any = JSON.parse(window.localStorage.getItem('timestamp')!);

                if (itsMoreThanXminutes(storage?.time, 4)) {
                    updateRestriction({ productId: simInfo?.productId, restrictionSpecId: card.index, activate: state })
                        .then(() => {
                            window.localStorage.setItem(
                                'timestamp',
                                JSON.stringify({ id: card.index, time: `${new Date()}` }),
                            );
                            sendToNative(state);
                        })
                        .catch((err: AxiosError) => {
                            console.error(err);
                        });
                } else {
                    dialog({
                        icon: <IconWarningRegular color={skinVars.colors.feedbackErrorBackground} size={64} />,
                        title: t('requestNotAvaiable'),
                        message: (() => {
                            return (
                                <Stack space={16}>
                                    <Text3 light color={skinVars.colors.borderHigh}>
                                        {t('notPosibleFinishedRequest')}
                                    </Text3>
                                    <Text3 light color={skinVars.colors.borderHigh}>
                                        {t('call1551')}
                                    </Text3>
                                </Stack>
                            );
                        })() as any,
                        onAccept() {},
                        acceptText: t('buttonUnderstoodFeedback'),
                    });
                }
            } else {
                updateRestriction({ productId: simInfo?.productId, restrictionSpecId: card.index, activate: state })
                    .then(() => {
                        window.localStorage.setItem(
                            'timestamp',
                            JSON.stringify({ id: card.index, time: `${new Date()}` }),
                        );
                        sendToNative(state);
                    })
                    .catch((err: AxiosError) => {
                        console.error(err);
                    });
            }
        }
    };

    const sendToNative = (_state: boolean) => {
        const action = _state ? 'activated' : 'desactivated';
        const type = _state ? 'feedback-info' : 'feedback-error';
        const literals: { [key: string]: any } = {
            activated: {
                title: `${t('blockingRequest')}`,
                subtitle: t('descriptionBlockedNumbersActivated'),
            },
            desactivated: {
                title: `${t('unlockingRequest')}`,
                subtitle: t('descriptionBlockedNumbersDeactivated'),
            },
        };
        const queryParams = {
            state: 'INFO',
            title: literals[action].title,
            subtitle: literals[action].subtitle,
            productId: simInfo?.productId,
            phone: simInfo?.number,
            multisim: simInfo?.multiSims,
        };

        const _productId = simInfo?.productId || state.productId;
        const _phone = simInfo?.number || state.phone;
        const _multisim = simInfo?.multiSims || state.multisim;

        if (
            navigateParams(type, `/configure-line/blocked-number-specials/feedback/${action}`, queryParams, {
                _storage,
            })
        ) {
            navigate(`/configure-line/blocked-number-specials/feedback/${action}`, {
                state: { productId: _productId, phone: _phone, multisim: _multisim },
            });
        }
    };

    const body = () => {
        return (
            <div className='blockedNumberSpecials-page'>
                <RowList>
                    {data.map((card, index) => {
                        return (
                            <div className='blockedNumberSpecials-page__card' key={index}>
                                <CardWithSwitch
                                    index={card.restrictionSpecId}
                                    title={card.title}
                                    subtitle={card.subtitle}
                                    state={card.state}
                                    onHandleSwicth={(ev: any) => handleSwitches(ev, index)}
                                />
                            </div>
                        );
                    })}
                </RowList>

                <Box>
                    <div className='blockedNumberSpecials-page__callout'>
                        <Callout
                            icon={<IconInformationUserRegular color={skinVars.colors.brand} size={24} />}
                            description={t('calloutRoaming')}
                            buttonLink={
                                <ButtonLink
                                    onPress={() => {
                                        const url =
                                            'https://o2online.es/ayuda/como-bloqueo-las-llamadas-a-numeros-especiales-desde-la-app/';
                                        if (navigateParams('modeExternal', url, {}, {})) {
                                            window.open(url, '_blank');
                                        }
                                    }}
                                >
                                    {t('moreInformation')}
                                </ButtonLink>
                            }
                        ></Callout>
                    </div>
                </Box>
            </div>
        );
    };

    return (
        <SkeletonMobile
            header={
                <div className='blockedNumberSpecials-page__header'>
                    {!(isWebviewAndroid || isWebviewIOS) && (
                        <>
                            <div className='blockedNumberSpecials-page__header-navigationBar'>
                                <NavigationBar
                                    isInverse={false}
                                    title={t('specialsNumber')}
                                    onBack={() => {
                                        navigate(`/configure-line`, {
                                            state: { productid: simInfo?.productId },
                                        });
                                    }}
                                />
                            </div>
                            <div
                                className='blockedNumberSpecials-page__header-return'
                                onClick={() => {
                                    navigate(`/configure-line`);
                                }}
                            >
                                <div className='blockedNumberSpecials-page__header-return-icon'>
                                    <IconChevronLeftRegular color={skinVars.colors.textPrimary} size={20} />
                                </div>
                                <Text2 medium color={skinVars.colors.textPrimary}>
                                    {t('returnResponsive')}
                                </Text2>
                            </div>
                        </>
                    )}
                    <div className='blockedNumberSpecials-page__breadcrumbs'>
                        <ThemeVariant>
                            <BreadCrumbsWrapper
                                title={t('specialsNumber')}
                                breadcrumbs={
                                    !isConvergent
                                        ? [
                                              { title: t('breadcrumbsHome'), url: '/home' },
                                              {
                                                  title: t('lineManagement'),
                                                  url: `/configure-line`,
                                              },
                                          ]
                                        : [
                                              { title: t('breadcrumbsHome'), url: '/home' },
                                              {
                                                  title: getRoleLine(JSON.parse(consumptions), simInfo?.productPsId!),
                                                  url: `/mobile?productPsId=${simInfo?.productPsId}`,
                                              },
                                              {
                                                  title: t('lineManagement'),
                                                  url: `/configure-line`,
                                              },
                                          ]
                                }
                            />
                        </ThemeVariant>
                    </div>
                    <div className='blockedNumberSpecials-page__header-number'>
                        <Text6 color={skinVars.colors.textPrimary}>{formatMobileNumber(simInfo?.number)}</Text6>
                    </div>
                </div>
            }
            body={loading ? <Spinner size={24} /> : body()}
        ></SkeletonMobile>
    );
};

export default BlockedNumberSpecials;
